import React, { useState } from 'react';
import { Upload, X, Camera, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useCreditReasons } from '@/hooks/useCreditReasons';
import { useCreateCreditRequest, useCreditRequestsByProduct } from '@/hooks/useCreditRequests';

interface CreditRequestFormProps {
  productId: string;
  productName: string;
  orderId: string;
  unitPrice?: number;
  maxQuantity?: number;
}

export const CreditRequestForm: React.FC<CreditRequestFormProps> = ({
  productId,
  productName,
  orderId,
  unitPrice = 0,
  maxQuantity = 1,
}) => {
  const { toast } = useToast();
  const { data: creditReasons } = useCreditReasons();
  const { data: existingCredits } = useCreditRequestsByProduct(productId);
  const createCreditRequest = useCreateCreditRequest();

  const [formData, setFormData] = useState({
    credit_reason_id: '',
    quantity: 1,
    notes: '',
  });
  const [photos, setPhotos] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${orderId}/${productId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('credit-photos')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('credit-photos')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      
      if (!isValidType) {
        toast({
          title: "Invalid file type",
          description: "Please upload only image files",
          variant: "destructive",
        });
        return false;
      }
      
      if (!isValidSize) {
        toast({
          title: "File too large",
          description: "Please upload images smaller than 5MB",
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    });

    setPhotos(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 photos
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.credit_reason_id) {
      toast({
        title: "Missing information",
        description: "Please select a credit reason",
        variant: "destructive",
      });
      return;
    }

    if (formData.quantity <= 0 || formData.quantity > maxQuantity) {
      toast({
        title: "Invalid quantity",
        description: `Quantity must be between 1 and ${maxQuantity}`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      // Upload photos
      const photoUrls: string[] = [];
      for (const photo of photos) {
        const url = await uploadPhoto(photo);
        photoUrls.push(url);
      }

      // Create credit request
      const totalAmount = unitPrice * formData.quantity;
      await createCreditRequest.mutateAsync({
        product_id: productId,
        product_name: productName,
        order_id: orderId,
        credit_reason_id: formData.credit_reason_id,
        quantity: formData.quantity,
        unit_price: unitPrice,
        total_amount: totalAmount,
        notes: formData.notes,
        photos: photoUrls,
        requested_by: 'POS User',
        reason: 'POS Credit Request',
        invoice_item_id: null,
      });

      // Reset form
      setFormData({
        credit_reason_id: '',
        quantity: 1,
        notes: '',
      });
      setPhotos([]);

      toast({
        title: "Success",
        description: "Credit request submitted successfully",
      });
    } catch (error) {
      console.error('Error creating credit request:', error);
      toast({
        title: "Error",
        description: "Failed to submit credit request",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const totalCreditAmount = formData.quantity * unitPrice;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Request Credit
        </CardTitle>
        <CardDescription>
          Submit a credit request for this product
        </CardDescription>
      </CardHeader>
      <CardContent>
        {existingCredits && existingCredits.length > 0 && (
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <h4 className="font-medium text-blue-900 mb-2">Existing Credit Requests</h4>
            <div className="space-y-2">
              {existingCredits.map((credit) => (
                <div key={credit.id} className="flex items-center justify-between text-sm">
                  <span>{credit.quantity} units - {credit.credit_reasons?.reason_name}</span>
                  <Badge variant={credit.status === 'pending' ? 'secondary' : 'default'}>
                    {credit.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Credit Reason *</label>
              <Select
                value={formData.credit_reason_id}
                onValueChange={(value) => setFormData({...formData, credit_reason_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {creditReasons?.map((reason) => (
                    <SelectItem key={reason.id} value={reason.id}>
                      {reason.reason_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Quantity *</label>
              <Input
                type="number"
                min="1"
                max={maxQuantity}
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Max available: {maxQuantity} units
              </p>
            </div>
          </div>

          {unitPrice > 0 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Credit Amount:</span>
                <span className="text-lg font-bold text-green-700">
                  ${totalCreditAmount.toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                {formData.quantity} units × ${unitPrice.toFixed(2)} per unit
              </p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional details about the credit request..."
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Photos (Optional)</label>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('photo-upload')?.click()}
                  disabled={photos.length >= 5}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Add Photos
                </Button>
                <span className="text-xs text-muted-foreground">
                  {photos.length}/5 photos (max 5MB each)
                </span>
              </div>
              
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />

              {photos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded-md border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removePhoto(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={uploading || !formData.credit_reason_id}
              className="flex-1"
            >
              {uploading ? (
                <>
                  <Upload className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Submit Credit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};